import React from 'react';
import { Modal, Button, Form, Row, Col, Table, Badge } from 'react-bootstrap';
import SearchableSelect from '../common/SearchableSelect';

const AdvancedRuleModal = ({ 
  show, 
  onHide, 
  editingRuleId,
  formData, 
  setFormData, 
  onSave, 
  accounts, 
  payees,
  classes,
  plTypes, 
  bsTypes = [],
  previewTxs,
  addCondition,
  removeCondition,
  updateCondition
}) => {
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton className={editingRuleId ? "bg-info text-white" : "bg-primary text-white"}>
        <Modal.Title>{editingRuleId ? 'Edit Automation Rule' : 'Advanced Rule Creator'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={onSave}>
        <Modal.Body>
          <Row>
            <Col md={8} className="mb-3">
              <Form.Label className="font-weight-bold small">Rule Name</Form.Label>
              <Form.Control 
                type="text" size="sm"
                value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                required 
              />
            </Col>
            <Col md={4} className="mb-3">
              <Form.Label className="font-weight-bold small">Apply to</Form.Label>
              <Form.Control as="select" size="sm" value={formData.transactionType} onChange={e => setFormData({...formData, transactionType: e.target.value})}>
                <option value="All">All transactions</option>
                <option value="Money Out">Money Out</option>
                <option value="Money In">Money In</option>
              </Form.Control>
            </Col>
          </Row>

          <div className="bg-light p-3 rounded mb-4 border">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                    <span className="small font-weight-bold mr-3">Transactions must match</span>
                    <Form.Control 
                        as="select" size="sm" style={{ width: '120px' }}
                        value={formData.logicalOperator} 
                        onChange={e => setFormData({...formData, logicalOperator: e.target.value})}
                    >
                        <option value="AND">All (AND)</option>
                        <option value="OR">Any (OR)</option>
                    </Form.Control>
                    <span className="small font-weight-bold ml-2">of these conditions:</span>
                </div>
                <Button variant="outline-primary" size="sm" onClick={addCondition}>
                    <i className="fas fa-plus mr-1"></i> Add Condition
                </Button>
            </div>

            {formData.conditions.map((cond, idx) => (
                <React.Fragment key={idx}>
                    {idx > 0 && (
                        <div className="d-flex align-items-center mb-2 mt-1">
                            <div className="flex-grow-1 border-top"></div>
                            <Badge variant="secondary" className="mx-3 small py-1" style={{ fontSize: '0.7rem' }}>
                                {formData.logicalOperator}
                            </Badge>
                            <div className="flex-grow-1 border-top"></div>
                        </div>
                    )}
                    <Row className="no-gutters mb-2 align-items-center">
                        <Col md={3} className="pr-1">
                            <Form.Control as="select" size="sm" value={cond.field} onChange={e => updateCondition(idx, 'field', e.target.value)}>
                                <option value="description">Description</option>
                                <option value="amount">Amount</option>
                            </Form.Control>
                        </Col>
                        <Col md={3} className="pr-1">
                            <Form.Control as="select" size="sm" value={cond.operator} onChange={e => updateCondition(idx, 'operator', e.target.value)}>
                                <option value="contains">Contains</option>
                                <option value="is exactly">Is Exactly</option>
                                <option value="does not contain">Does Not Contain</option>
                                {cond.field === 'amount' && (
                                    <>
                                        <option value="is greater than">Is Greater Than</option>
                                        <option value="is less than">Is Less Than</option>
                                    </>
                                )}
                            </Form.Control>
                        </Col>
                        <Col className="pr-1">
                            <Form.Control 
                                type={cond.field === 'amount' ? 'number' : 'text'} 
                                step="0.01" size="sm"
                                value={cond.value} onChange={e => updateCondition(idx, 'value', e.target.value)}
                                placeholder="Value"
                                required 
                            />
                        </Col>
                        <Col xs="auto">
                            <Button variant="link" className="text-danger p-0 ml-2" onClick={() => removeCondition(idx)} disabled={formData.conditions.length === 1}>
                                <i className="fas fa-trash-alt"></i>
                            </Button>
                        </Col>
                    </Row>
                </React.Fragment>
            ))}
          </div>

          <Row>
            <Col md={4} className="mb-4">
              <Form.Label className="font-weight-bold small">Assign to Category</Form.Label>
              <SearchableSelect
                options={[
                    { value: 'NEW_CATEGORY', label: '+ Add New Category...', sublabel: 'Create a new account' },
                    ...accounts.filter(a => plTypes.includes(a.type)).map(acc => ({ value: acc._id, label: acc.name, sublabel: `Category (${acc.type})` })),
                    ...accounts.filter(a => bsTypes.includes(a.type)).map(acc => ({ value: acc._id, label: acc.name, sublabel: `Transfer (${acc.type})` }))
                ]}
                value={formData.targetAccountId}
                onChange={e => setFormData({...formData, targetAccountId: e.target.value})}
                placeholder="-- Select --"
              />
            </Col>
            <Col md={4} className="mb-4">
              <Form.Label className="font-weight-bold small">Assign to Payee</Form.Label>
              <SearchableSelect
                options={[
                    { value: '', label: '-- None --' },
                    { value: 'NEW_PAYEE', label: '+ Add New Payee...', sublabel: 'Create a new payee' },
                    ...(payees ? payees.filter(p => p.isActive).map(p => ({ value: p._id, label: p.name, sublabel: p.type })) : [])
                ]}
                value={formData.targetPayeeId || ''}
                onChange={e => setFormData({...formData, targetPayeeId: e.target.value})}
                placeholder="-- None --"
              />
            </Col>
            <Col md={4} className="mb-4">
              <Form.Label className="font-weight-bold small">Assign to Class</Form.Label>
              <SearchableSelect
                options={[
                    { value: '', label: '-- None --' },
                    ...(classes ? classes.filter(c => c.isActive).map(c => ({ value: c._id, label: c.name })) : [])
                ]}
                value={formData.targetClassId || ''}
                onChange={e => setFormData({...formData, targetClassId: e.target.value})}
                placeholder="-- None --"
              />
            </Col>

            {formData.targetAccountId === 'NEW_CATEGORY' && (
              <Col md={12} className="mb-4">
                <div className="bg-light p-3 border rounded shadow-sm border-primary">
                  <h6 className="small font-weight-bold text-primary text-uppercase mb-3">Quick Add Category</h6>
                  <Row>
                    <Col md={7}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small font-weight-bold">New Category Name</Form.Label>
                        <Form.Control 
                          type="text" size="sm" 
                          value={formData.newCategoryName} 
                          onChange={e => setFormData({...formData, newCategoryName: e.target.value})}
                          placeholder="e.g. Subscriptions"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small font-weight-bold">Type</Form.Label>
                        <Form.Control 
                          as="select" size="sm" 
                          value={formData.newCategoryType} 
                          onChange={e => setFormData({...formData, newCategoryType: e.target.value})}
                          required
                        >
                          {plTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>
            )}

            {formData.targetPayeeId === 'NEW_PAYEE' && (
              <Col md={12} className="mb-4">
                <div className="bg-light p-3 border rounded shadow-sm border-info">
                  <h6 className="small font-weight-bold text-info text-uppercase mb-3">Quick Add Payee</h6>
                  <Row>
                    <Col md={7}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small font-weight-bold">New Payee Name</Form.Label>
                        <Form.Control 
                          type="text" size="sm" 
                          value={formData.newPayeeName || ''} 
                          onChange={e => setFormData({...formData, newPayeeName: e.target.value})}
                          placeholder="e.g. Acme Corp"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-0">
                        <Form.Label className="small font-weight-bold">Payee Type</Form.Label>
                        <Form.Control 
                          as="select" size="sm" 
                          value={formData.newPayeeType || 'Vendor'} 
                          onChange={e => setFormData({...formData, newPayeeType: e.target.value})}
                          required
                        >
                          <option value="Vendor">Vendor</option>
                          <option value="Customer">Customer</option>
                          <option value="Employee">Employee</option>
                          <option value="Other">Other</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>
            )}
          </Row>

          {previewTxs.length > 0 && (
            <div className="bg-light p-3 rounded border">
              <h6 className="text-muted small text-uppercase mb-3 font-weight-bold">Live Preview (Matches: {previewTxs.length}+)</h6>
              <Table size="sm" borderless className="mb-0">
                <tbody>
                  {previewTxs.map(tx => (
                    <tr key={tx._id} className="small">
                      <td>{new Date(tx.date).toLocaleDateString()}</td>
                      <td className="text-truncate" style={{ maxWidth: '300px' }}>{tx.description}</td>
                      <td className="text-right font-weight-bold">${tx.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onHide}>Cancel</Button>
          <Button variant={editingRuleId ? "info" : "primary"} size="sm" type="submit">
            {editingRuleId ? 'Update & Apply Rule' : 'Save & Apply Rule'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AdvancedRuleModal;
